/**
 * Tabs that never wait (screenshots/41 card 12):
 *  - hover/focus intent (100ms) prefetches the tab's data into its store, so a click paints instantly
 *  - each tab remembers its scroll position
 *  - number keys 1–9 switch tabs (ignored while typing)
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { recipes } from "../motion";
import { scrollToTop, scrollTop, viewTop } from "../app/scroller";

export const isTyping = (e: KeyboardEvent) => {
  const t = e.target as HTMLElement | null;
  return !!t && (t.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(t.tagName));
};

/**
 * Where to scroll for a tab not visited yet: keep the reader where they are, but if the panel's top has scrolled
 * up under (sticky) tabs, or the tabs themselves are above the window, bring the panel's start back into view.
 */
function firstVisitScroll(from?: HTMLElement | null): number {
  const list = from?.closest<HTMLElement>('[role="tablist"]');
  if (!list) return scrollTop();
  const lr = list.getBoundingClientRect(), top = viewTop();
  if (lr.top < top) return Math.max(0, scrollTop() + lr.top - top - 16);
  const panel = list.closest(".page, main")?.querySelector<HTMLElement>('[role="tabpanel"]');
  const under = panel ? panel.getBoundingClientRect().top - lr.bottom : 0;   // < 0: the panel's start is hidden under the tabs
  return Math.max(0, Math.min(scrollTop(), scrollTop() + under));
}

const modalOpen = () => !!document.querySelector('[aria-modal="true"]');

export function useTabs<T extends string>(tabs: readonly T[], active: T, setActive: (t: T) => void, prefetch?: (t: T) => void) {
  const scroll = useRef(new Map<T, number>());
  const timer = useRef<number | undefined>(undefined);
  const prefetched = useRef(new Set<T>());

  const select = useCallback((t: T, from?: HTMLElement | null) => {
    // Re-selecting the active tab still calls setActive: a page may show a tab as active while a narrower filter is
    // on (People: "Everyone" with allies selected), and clicking it must clear that.
    if (t === active) { setActive(t); return; }
    scroll.current.set(active, scrollTop());
    const saved = scroll.current.get(t);
    const top = saved ?? (from ? firstVisitScroll(from) : scrollTop());
    setActive(t);
    requestAnimationFrame(() => scrollToTop(top));
  }, [active, setActive]);

  const intent = (t: T) => ({
    onPointerEnter: () => {
      if (!prefetch || prefetched.current.has(t)) return;
      timer.current = window.setTimeout(() => { prefetched.current.add(t); prefetch(t); }, recipes.tabs.prefetchHoverMs);
    },
    onPointerLeave: () => window.clearTimeout(timer.current),
    onFocus: () => { if (prefetch && !prefetched.current.has(t)) { prefetched.current.add(t); prefetch(t); } },
  });

  const ids = useRef(tabs);
  ids.current = tabs;
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      // Not while typing, and not while a dialog (shortcut sheet, drawer) is open over the page.
      if (isTyping(e) || e.metaKey || e.ctrlKey || e.altKey || modalOpen()) return;
      const n = Number(e.key);
      if (n >= 1 && n <= ids.current.length) { e.preventDefault(); select(ids.current[n - 1]); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [select]);

  /** Props for <button role="tab">. */
  const tabProps = (t: T) => ({
    role: "tab" as const,
    "aria-selected": t === active,
    tabIndex: t === active ? 0 : -1,
    onClick: (e: React.MouseEvent<HTMLElement>) => select(t, e.currentTarget),
    ...intent(t),
  });
  return { tabProps, select };
}

/** Arrow keys for any role="tablist" (WAI-ARIA tabs, automatic activation): ←/→ (and ↑/↓), Home, End move to the
 *  neighbouring tab, focus it and select it. Put it on the tablist's onKeyDown. */
export function onTablistKey(e: React.KeyboardEvent<HTMLElement>) {
  const keys: Record<string, number> = { ArrowRight: 1, ArrowDown: 1, ArrowLeft: -1, ArrowUp: -1, Home: -Infinity, End: Infinity };
  if (!(e.key in keys) || e.metaKey || e.ctrlKey || e.altKey) return;
  const tabs = Array.from(e.currentTarget.querySelectorAll<HTMLElement>('[role="tab"]:not([disabled])'));
  const i = tabs.findIndex((t) => t === document.activeElement || t.contains(document.activeElement));
  if (i < 0) return;
  e.preventDefault();
  const step = keys[e.key];
  const next = tabs[step === -Infinity ? 0 : step === Infinity ? tabs.length - 1 : (i + step + tabs.length) % tabs.length];
  next.focus();
  next.click();
}

/**
 * j/k move between rows marked [data-row], ↵ opens, plus single-letter actions (t, w, f…).
 * Focus moves to the row element so the focus ring follows (no scroll animation on keyboard nav).
 */
export function useListKeyboard(
  container: React.RefObject<HTMLElement | null>,
  actions: { open?: (id: string) => void; [key: string]: ((id: string) => void) | undefined },
) {
  const ref = useRef(actions);
  ref.current = actions;
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const root = container.current;
      if (!root || isTyping(e) || e.metaKey || e.ctrlKey || e.altKey || modalOpen()) return;
      if (e.key === "?" && ref.current["?"]) { e.preventDefault(); ref.current["?"]!(""); return; }   // shortcut sheet (screenshots/41 card 10)
      const rows = Array.from(root.querySelectorAll<HTMLElement>("[data-row]"));
      if (!rows.length) return;
      const i = rows.findIndex((r) => r === document.activeElement || r.contains(document.activeElement));
      const id = i >= 0 ? rows[i].dataset.row! : undefined;
      if (e.key === "j" || e.key === "k") {
        e.preventDefault();
        const next = rows[Math.max(0, Math.min(rows.length - 1, i + (e.key === "j" ? 1 : -1)))] ?? rows[0];
        next.focus({ preventScroll: false });
        return;
      }
      if (!id) return;
      // Enter on a control inside the row (Follow, Show more, a What-if radio) is that control's, not the row's.
      const onControl = document.activeElement !== rows[i] && !!(document.activeElement as HTMLElement | null)?.matches?.('a[href], button, input, select, textarea, summary, [role="radio"], [role="checkbox"], [role="button"]');
      if (e.key === "Enter") { if (onControl) return; e.preventDefault(); ref.current.open?.(id); return; }
      const fn = ref.current[e.key];
      if (fn) { e.preventDefault(); fn(id); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [container]);
}

/**
 * Condensed sticky header: true once `sentinel` (e.g. the stepper) has scrolled out of view, or, with `offset`
 * (px, read when the page mounts), once its bottom has passed that line, so the bar can appear exactly as the
 * content below the sentinel reaches it.
 */
export function useScrolledPast(sentinel: React.RefObject<HTMLElement | null>, offset: () => number = () => 0) {
  const [past, setPast] = useState(false);
  useEffect(() => {
    const el = sentinel.current;
    if (!el) return;
    const line = Math.max(0, Math.round(offset()));
    const io = new IntersectionObserver(([e]) => setPast(!e.isIntersecting && e.boundingClientRect.top < line), { rootMargin: `-${line}px 0px 0px 0px` });
    io.observe(el);
    return () => io.disconnect();
  }, [sentinel, setPast]); // eslint-disable-line react-hooks/exhaustive-deps
  return past;
}

